import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { ButtonDirective } from 'primeng/button';
import { MediaPickerComponent } from '../media-picker/media-picker';
import { SectionCardComponent } from '../section-card/section-card';
import { EditorFilesService } from '../../../features/editor/services/editor-files.service';
import { SubtitleEntry, SubtitleSourcesMap, FileCategory } from '../../models';
import { deriveSlug, makeUniqueSlug } from '../../utils/slug';
import { cloneDeep } from '../../utils/clone';

interface ChapterOption {
	slug: string;
	name?: string;
}

@Component({
	selector: 'app-subtitle-track-editor',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, InputText, ButtonDirective, MediaPickerComponent, SectionCardComponent],
	templateUrl: './subtitle-track-editor.html',
	styleUrl: './subtitle-track-editor.scss',
})
export class SubtitleTrackEditorComponent {
	subtitles = input.required<SubtitleEntry[]>();
	subtitleSources = input<SubtitleSourcesMap>({});
	chapters = input<ChapterOption[]>([]);

	subtitlesChange = output<SubtitleEntry[]>();
	subtitleSourcesChange = output<SubtitleSourcesMap>();

	/** Chapters to show a per-chapter file-picker row for. A chapterless project
	 *  still plays subtitles through the player's synthetic `default` chapter
	 *  (`player` repo `project.service.ts`), so surface a single `default` row
	 *  instead of a dead "no chapters" state. The backend route and publish step
	 *  key on whatever chapterSlug lands in `subtitleSources`, so `default`
	 *  needs no backend change. */
	protected readonly effectiveChapters = computed<ChapterOption[]>(() => {
		const real = this.chapters();
		return real.length ? real : [{ slug: 'default', name: 'Whole project (no chapters)' }];
	});

	protected expandedTrack = signal<number | null>(null);
	protected pickerVisible = signal(false);
	protected pickerCurrentValue = signal('');
	protected readonly pickerCategories: FileCategory[] = ['subtitles'];
	private pickerTarget: { trackSlug: string; chapterSlug: string } | null = null;

	private filesService = inject(EditorFilesService);

	protected toggleExpand(index: number, event: MouseEvent): void {
		event.stopPropagation();
		this.expandedTrack.update(current => (current === index ? null : index));
	}

	protected addTrack(): void {
		const label = prompt('Subtitle track label (e.g. Czech):');
		if (!label?.trim()) return;
		const trimmed = label.trim();
		const existingSlugs = new Set(this.subtitles().map(t => t.slug));
		const slug = makeUniqueSlug(deriveSlug(trimmed) || 'track', existingSlugs);
		const next = [...this.subtitles(), { slug, label: trimmed }];
		this.subtitlesChange.emit(next);
		this.expandedTrack.set(next.length - 1);
	}

	protected removeTrack(index: number, event: MouseEvent): void {
		event.stopPropagation();
		if (!confirm('Delete this subtitle track?')) return;
		const next = [...this.subtitles()];
		next.splice(index, 1);
		// Emit only the track change. The removed track's `subtitleSources`
		// entries are pruned by the parent's `onSubtitlesChange` in the SAME
		// patch — emitting a second `subtitleSourcesChange` here would be
		// clobbered by the first (the parent's `data` input hasn't propagated
		// back yet, so the second patch would clone the pre-removal state).
		this.subtitlesChange.emit(next);
		if (this.expandedTrack() === index) this.expandedTrack.set(null);
	}

	protected setTrackLabel(index: number, label: string): void {
		const next = [...this.subtitles()];
		const current = next[index];
		if (!current) return;
		next[index] = { ...current, label };
		this.subtitlesChange.emit(next);
	}

	protected fileNameFor(trackSlug: string, chapterSlug: string): string | null {
		const baseUrl = this.subtitleSources()[trackSlug]?.[chapterSlug];
		if (!baseUrl) return null;
		return this.filesService.findByBaseUrl(baseUrl)?.name ?? baseUrl.split('/').pop() ?? baseUrl;
	}

	protected openChapterPicker(trackSlug: string, chapterSlug: string): void {
		this.pickerTarget = { trackSlug, chapterSlug };
		this.pickerCurrentValue.set(this.subtitleSources()[trackSlug]?.[chapterSlug] ?? '');
		this.pickerVisible.set(true);
	}

	protected onChapterFilePicked(baseUrl: string): void {
		if (!this.pickerTarget) return;
		const { trackSlug, chapterSlug } = this.pickerTarget;
		const sources = cloneDeep(this.subtitleSources());
		sources[trackSlug] = { ...(sources[trackSlug] ?? {}), [chapterSlug]: baseUrl };
		this.subtitleSourcesChange.emit(sources);
	}

	protected clearChapterFile(trackSlug: string, chapterSlug: string, event: MouseEvent): void {
		event.stopPropagation();
		const sources = cloneDeep(this.subtitleSources());
		if (sources[trackSlug]) delete sources[trackSlug][chapterSlug];
		this.subtitleSourcesChange.emit(sources);
	}
}
